import { isEmpty } from "@bodil/core/assert";
import { attribute, attributeGetter, Component } from "@bodil/dom/component";
import * as mdi from "@mdi/js";
import type { Role } from "aria-attribute-types";
import { html } from "lit";
import { customElement } from "lit/decorators.js";

import styles from "./icon.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-icon": BtkIcon;
    }
}

const missing = mdi.mdiHeartBroken;

const iconMap = {
    ok: [mdi.mdiCheckCircle, mdi.mdiCheckCircleOutline],
    cancel: [mdi.mdiCancel],
    next: [mdi.mdiArrowRightBoldCircle, mdi.mdiArrowRightBoldCircleOutline],
    previous: [mdi.mdiArrowLeftBoldCircle, mdi.mdiArrowLeftBoldCircleOutline],
    add: [mdi.mdiPlusCircle, mdi.mdiPlusCircleOutline],
    edit: [mdi.mdiPencilBox, mdi.mdiPencilBoxOutline],
    delete: [mdi.mdiDeleteForever, mdi.mdiDeleteForeverOutline],
    search: [mdi.mdiMagnify],
    retry: [mdi.mdiSync],
    reload: [mdi.mdiReload],
    login: [mdi.mdiLogin],
    logout: [mdi.mdiLogout],
    settings: [mdi.mdiCog, mdi.mdiCogOutline],
    menu: [mdi.mdiMenu],
    home: [mdi.mdiHome, mdi.mdiHomeOutline],
    top: [mdi.mdiArrowCollapseUp],
    bottom: [mdi.mdiArrowCollapseDown],
    caret: [mdi.mdiMenuDown, mdi.mdiMenuDownOutline],
    undo: [mdi.mdiUndo],
    redo: [mdi.mdiRedo],
    close: [mdi.mdiCloseCircle, mdi.mdiCloseCircleOutline],
    help: [mdi.mdiHelpCircle, mdi.mdiHelpCircleOutline],
    debug: [mdi.mdiShieldBug, mdi.mdiShieldBugOutline],

    info: [mdi.mdiAlertOctagon, mdi.mdiAlertOctagonOutline],
    warning: [mdi.mdiAlert, mdi.mdiAlertOutline],
    error: [mdi.mdiAlertCircle, mdi.mdiAlertCircleOutline],
    fatal: [mdi.mdiBomb],
    missing: [mdi.mdiHelpBox, mdi.mdiHelpBoxOutline],

    dog: [mdi.mdiDog],
    cat: [mdi.mdiCat],
};

export function registerIcons(icons: Record<string, [string] | [string, string]>) {
    for (const [key, value] of Object.entries(icons)) {
        (iconMap as any)[key] = value;
    }
}

export type BtkIconMap = {
    [K in Extract<keyof typeof iconMap, string>]: [string] | [string, string];
};

export type BtkIconName = keyof BtkIconMap;
export type BtkIconRotate = "0" | "90" | "180" | "270";

@customElement("btk-icon")
export class BtkIcon extends Component {
    @attribute accessor name: BtkIconName | null = null;
    @attribute accessor path: string | null = null;
    @attribute accessor src: string | null = null;
    @attribute({ type: Boolean }) accessor outline = false;
    @attribute accessor rotate: BtkIconRotate | null = null;
    @attribute accessor label: string | null = null;

    @attributeGetter({ reactive: true }) get role(): Role | null {
        return isEmpty(this.label) ? null : "img";
    }
    @attributeGetter({ reactive: true }) get ariaLabel(): string | null {
        return this.label;
    }
    @attributeGetter({ reactive: true }) get ariaHidden(): string | null {
        return isEmpty(this.label) ? "true" : null;
    }

    protected override render(): unknown {
        if (!isEmpty(this.src)) {
            return html`<svg><use href=${this.src}></use></svg>`;
        }
        return html`<svg viewbox="0 0 24 24" part="svg">
            <path
                d=${this.path ??
                (isEmpty(this.name) || iconMap[this.name] === undefined
                    ? missing
                    : this.outline
                      ? (iconMap[this.name][1] ?? iconMap[this.name][0])
                      : iconMap[this.name][0])}
            ></path>
        </svg>`;
    }

    static styles = styles;
}
